import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../Includes/Navbar/Navbar";
import Sidebar from "../../Includes/Sidebar/Sidebar";
import UserServices from "../../../UserServices/UserServices";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const fetchAttendance = async ({ start_date, end_date, branch_id = "", gender = "", start_time = "", end_time = "", limit = 100, page = 1 }) => {
  try {
    const resp = await UserServices.getClientAttendence(
      "Member",
      "1",
      branch_id,
      {
        sdate: start_date,
        edate: end_date,
        start_time,
        end_time,
        gender,
      },
      "",
      limit,
      page
    );
    if (resp.status === 200) {
      return resp.data.data?.data || [];
    }
    return [];
  } catch (e) {
    return [];
  }
};

const formatBranch = (uid = "") => {
  const m = uid.match(/^([A-Za-z]+)(\d+)/);
  return m ? `${m[1]}-${m[2]}` : uid;
};
const formatDate = (iso = "") => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function FootfallBranchReport({ initialFilters, embedded = false }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [filters, setFilters] = useState(initialFilters || { start_date: today, end_date: today, branch_id: "", gender: "", start_time: "", end_time: "", limit: 25, page: 1 });
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tempFilters, setTempFilters] = useState(() => ({ ...filters }));

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const multiDay = filters.start_date !== filters.end_date;
        if (!multiDay) {
          setRaw(await fetchAttendance(filters));
        } else {
          const getDateList = (startIso, endIso) => {
            const out = [];
            let cur = new Date(startIso);
            const end = new Date(endIso);
            while (cur <= end) {
              out.push(cur.toISOString().slice(0, 10));
              cur.setDate(cur.getDate() + 1);
            }
            return out;
          };
          const days = getDateList(filters.start_date, filters.end_date);
          const results = await Promise.all(
            days.map((d) =>
              fetchAttendance({ ...filters, start_date: d, end_date: d, page: 1, limit: 1000 })
            )
          );
          setRaw(results.flat());
        }
      } catch (_) {
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [filters]);

  const branches = useMemo(() => {
    const set = new Set();
    raw.forEach((r) => set.add(formatBranch(r.attendee?.uid || "")));
    return Array.from(set);
  }, [raw]);

  const dailyByBranch = useMemo(() => {
    const map = new Map();
    raw.forEach((r) => {
      const d = formatDate(r.date || "");
      const br = formatBranch(r.attendee?.uid || "");
      if (!d || !br) return;
      const row = map.get(d) || { date: d };
      row[br] = (row[br] || 0) + 1;
      map.set(d, row);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [raw]);

  // Grouped by date for multi-day layout and per-date branch counts
  const groupedByDate = useMemo(() => {
    const map = {};
    raw.forEach((r) => {
      const d = r.date || "";
      if (!d) return;
      map[d] = map[d] || [];
      map[d].push(r);
    });
    return map;
  }, [raw]);

  const perDateBranchCounts = useMemo(() => {
    const out = {};
    Object.keys(groupedByDate).forEach((d) => {
      const counts = {};
      groupedByDate[d].forEach((r) => {
        const br = formatBranch(r.attendee?.uid || "");
        counts[br] = (counts[br] || 0) + 1;
      });
      out[d] = Object.keys(counts).map((br) => ({ branch: br, count: counts[br] }));
    });
    return out;
  }, [groupedByDate]);

  const topBranchCounts = useMemo(() => {
    const counts = {};
    raw.forEach((r) => {
      const br = formatBranch(r.attendee?.uid || "");
      counts[br] = (counts[br] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [raw]);

  const onTempChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((f) => ({ ...f, [name]: value }));
  };
  const applyFilters = () => {
    setFilters((prev) => ({ ...prev, ...tempFilters, page: 1 }));
  };

  // Use API pagination; display the page returned by the server
  const paged = raw;

  const content = (
    <div className="container-fluid">
      <div className="card mb-3"><div className="card-body">
        <form className="row g-2" aria-label="Footfall branch filters">
          <div className="col-12 col-md-3">
            <label className="form-label">Start date</label>
            <input name="start_date" type="date" className="form-control" value={tempFilters.start_date} onChange={onTempChange} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">End date</label>
            <input name="end_date" type="date" className="form-control" value={tempFilters.end_date} onChange={onTempChange} />
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">Branch</label>
            <select name="branch_id" className="form-select" value={tempFilters.branch_id} onChange={onTempChange}>
              <option value="">All</option>
              <option value="15">F-11</option>
              <option value="1">G-13</option>
            </select>
          </div>
          <div className="col-12 col-md-2">
            <label className="form-label">Gender</label>
            <select name="gender" className="form-select" value={tempFilters.gender} onChange={onTempChange}>
              <option value="">All</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div className="col-6 col-md-1">
            <label className="form-label">Limit</label>
            <input name="limit" type="number" min="5" step="5" className="form-control" value={tempFilters.limit} onChange={onTempChange} />
          </div>
          <div className="col-6 col-md-1 d-flex align-items-end">
            <button type="button" className="btn btn-primary w-100" onClick={applyFilters}>Go</button>
          </div>
          <div className="col-6 col-md-1 d-flex align-items-end">
            <button type="button" className="btn btn-outline-secondary w-100" onClick={() => {
              const headers = ["Date","Branch","Gender","Name"]; const rows = raw.map(r => [formatDate(r.date), formatBranch(r.attendee?.uid||""), r.gender||"", `${r.attendee?.first_name||""} ${r.attendee?.last_name||""}`.trim()]); const csv=[headers,...rows].map(r=>r.map(v=>`"${String(v).replaceAll('"','""')}"`).join(",")).join("\n"); const blob=new Blob([csv],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=`footfall_branch_${filters.start_date}_${filters.end_date}.csv`; a.click(); URL.revokeObjectURL(url);
            }}>CSV</button>
          </div>
        </form>
      </div></div>

      {/* Summary cards */}
      <div className="row g-3">
        {topBranchCounts.map(([b, c], i) => (
          <div className="col-12 col-md-4" key={i}><div className="card h-100"><div className="card-body"><div className="text-muted">Top Branch #{i+1}</div><div className="fw-bold fs-5">{b}</div><div className="small">Entries: {c}</div></div></div></div>
        ))}
      </div>

      <div className="card my-3"><div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Branch-wise entry trends</h6>
          {loading ? <small className="text-muted">Loading…</small> : null}
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={dailyByBranch} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" interval={0} tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {branches.map((b, i) => (
                <Line key={b} type="monotone" dataKey={b} stroke={["#0088FE","#FF6384","#00C49F","#FFBB28","#845EC2"][i%5]} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div></div>

      {/* Detail: single-day paginated OR multi-day rows (chart left, table right) */}
      {filters.start_date === filters.end_date ? (
        <div className="card"><div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Detail</h6>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-light" onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, Number(f.page) - 1) }))}>Prev</button>
              <span className="align-self-center small">Page {filters.page}</span>
              <button type="button" className="btn btn-light" onClick={() => setFilters((f) => ({ ...f, page: Number(f.page) + 1 }))}>Next</button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead><tr><th>Date</th><th>Branch</th><th>Gender</th><th>Name</th></tr></thead>
              <tbody>
                {paged.map((r, idx) => (
                  <tr key={idx}><td>{formatDate(r.date)}</td><td>{formatBranch(r.attendee?.uid||"")}</td><td>{r.gender||""}</td><td>{`${r.attendee?.first_name||""} ${r.attendee?.last_name||""}`.trim()}</td></tr>
                ))}
                {!paged.length && (<tr><td colSpan={4} className="text-center text-muted">No records</td></tr>)}
              </tbody>
            </table>
          </div>
        </div></div>
      ) : (
        <div className="row g-3">
          {Object.keys(groupedByDate).sort().map((d) => (
            <div className="col-12" key={d}>
              <div className="row g-3">
                <div className="col-12 col-lg-6">
                  <div className="card h-100"><div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{formatDate(d)}</h6>
                      <span className="badge bg-primary">Total: {groupedByDate[d].length}</span>
                    </div>
                    <div style={{ width: "100%", height: 280 }}>
                      <ResponsiveContainer>
                        <LineChart data={perDateBranchCounts[d]} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="branch" interval={0} tick={{ fontSize: 12 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="count" stroke="#0088FE" dot />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div></div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="card h-100"><div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: 380, overflowY: 'auto' }}>
                      <table className="table table-sm align-middle">
                        <thead><tr><th>Date</th><th>Branch</th><th>Gender</th><th>Name</th></tr></thead>
                        <tbody>
                          {groupedByDate[d].map((r, idx) => (
                            <tr key={idx}><td>{formatDate(r.date)}</td><td>{formatBranch(r.attendee?.uid||"")}</td><td>{r.gender||""}</td><td>{`${r.attendee?.first_name||""} ${r.attendee?.last_name||""}`.trim()}</td></tr>
                          ))}
                          {!groupedByDate[d].length && (<tr><td colSpan={4} className="text-center text-muted">No records</td></tr>)}
                        </tbody>
                      </table>
                    </div>
                  </div></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div className='wrapper'>
      <Navbar/>
      <Sidebar/>
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          {content}
        </div>
      </main>
    </div>
  );
}


